import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  getCalendars, getCalendarSettings, saveCalendarSettings,
  getContactsCacheStatus, refreshContacts,
  type CalendarOption, type CalendarSettings, type ContactsCacheStatus,
} from '../../lib/api'
import { useTheme } from '../../lib/useTheme'
import { usePushSubscription } from '../../lib/usePushSubscription'
import { authClient } from '../../lib/auth'
import { PageHeader } from '../../components/PageHeader'
import { m } from '../../paraglide/messages'
import { getLocale, setLocale, locales, type Locale } from '../../paraglide/runtime'

export const Route = createFileRoute('/settings/')({
  component: SettingsPage,
})

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border border-zinc-200 p-4 mb-6">
      <h2 className="text-sm font-semibold mb-3">{title}</h2>
      {children}
    </section>
  )
}

function SettingsPage() {
  return (
    <div className="max-w-xl">
      <PageHeader title={m.settings_title()} />
      <Appearance />
      <Language />
      <Notifications />
      <GoogleAccount />
      <Contacts />
      <CalendarConfig />
    </div>
  )
}

// ─── Appearance (theme) ───────────────────────────────────────────────────────

function Appearance() {
  const [dark, setDark] = useTheme()
  return (
    <Section title={m.settings_appearance()}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-zinc-600">
          {m.settings_theme({ mode: dark ? m.settings_theme_dark() : m.settings_theme_light() })}
        </span>
        <button
          onClick={() => setDark(d => !d)}
          className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
        >
          {m.settings_toggle()}
        </button>
      </div>
    </Section>
  )
}

// ─── Language (locale) ────────────────────────────────────────────────────────

const localeLabel: Record<string, () => string> = {
  en: () => m.lang_en(),
  he: () => m.lang_he(),
}

function Language() {
  const current = getLocale()
  return (
    <Section title={m.settings_language()}>
      <div className="flex gap-2">
        {locales.map((loc) => (
          <button
            key={loc}
            // setLocale persists to localStorage and reloads, re-rendering all
            // messages and re-applying dir before paint (#43/#44).
            onClick={() => loc !== current && setLocale(loc as Locale)}
            className={
              loc === current
                ? 'flex-1 bg-zinc-900 text-white px-3 py-1.5 text-sm'
                : 'flex-1 border border-zinc-300 px-3 py-1.5 text-sm hover:bg-zinc-50'
            }
          >
            {(localeLabel[loc] ?? (() => loc))()}
          </button>
        ))}
      </div>
    </Section>
  )
}

// ─── Google Account ───────────────────────────────────────────────────────────

function GoogleAccount() {
  const { data: session } = authClient.useSession()
  const accountsQ = useQuery({
    queryKey: ['auth-accounts'],
    queryFn: async () => {
      const res = await authClient.listAccounts()
      if (res.error) throw new Error(res.error.message)
      return res.data ?? []
    },
  })

  const googleLinked = accountsQ.data?.some((a: { providerId: string }) => a.providerId === 'google') ?? false

  const connect = () => authClient.linkSocial({ provider: 'google', callbackURL: '/settings' })

  return (
    <Section title={m.settings_google_section()}>
      {accountsQ.isLoading ? (
        <p className="text-sm text-zinc-400">{m.loading()}</p>
      ) : googleLinked ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-zinc-600">
            {m.settings_google_connected({ email: session?.user.email ?? '' })}
          </span>
          <button
            onClick={connect}
            className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100 shrink-0"
          >
            {m.settings_google_reconnect()}
          </button>
        </div>
      ) : (
        <button
          onClick={connect}
          className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
        >
          {m.settings_google_connect()}
        </button>
      )}
      {accountsQ.error && (
        <p className="text-sm text-red-600 mt-2">{(accountsQ.error as Error).message}</p>
      )}
    </Section>
  )
}

// ─── Notifications (push) ─────────────────────────────────────────────────────

function Notifications() {
  const push = usePushSubscription()
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null)

  async function runTest() {
    setTestMsg(null)
    try {
      await push.test()
      setTestMsg({ ok: true, text: m.settings_test_sent() })
    } catch (err) {
      setTestMsg({ ok: false, text: err instanceof Error ? err.message : m.settings_test_failed() })
    }
  }

  return (
    <Section title={m.settings_notifications()}>
      {push.state === 'unsupported' ? (
        <p className="text-sm text-zinc-500">{m.settings_push_unsupported()}</p>
      ) : push.state === 'denied' ? (
        <p className="text-sm text-red-600">{m.settings_push_denied()}</p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-zinc-600">
              {push.state === 'subscribed' ? m.settings_push_on() : m.settings_push_off()}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={runTest}
                disabled={push.state === 'loading'}
                className="border border-zinc-300 px-3 py-1.5 text-sm hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {m.settings_test()}
              </button>
              <button
                onClick={push.state === 'subscribed' ? push.unsubscribe : push.subscribe}
                disabled={push.state === 'loading'}
                className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {push.state === 'loading' ? '…'
                  : push.state === 'subscribed' ? m.settings_turn_off()
                  : m.settings_enable()}
              </button>
            </div>
          </div>
          {testMsg && (
            <p className={`text-sm ${testMsg.ok ? 'text-zinc-600' : 'text-red-600'}`}>
              {testMsg.text}
            </p>
          )}
        </div>
      )}
    </Section>
  )
}

// ─── Contacts cache ───────────────────────────────────────────────────────────

function Contacts() {
  const queryClient = useQueryClient()
  const statusQ = useQuery<ContactsCacheStatus>({
    queryKey: ['contacts-cache-status'],
    queryFn:  getContactsCacheStatus,
  })

  const refresh = useMutation({
    mutationFn: refreshContacts,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contacts-cache-status'] }),
  })

  const synced = statusQ.data?.syncedAt
    ? new Date(statusQ.data.syncedAt).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' })
    : m.settings_never()

  return (
    <Section title={m.settings_contacts()}>
      <p className="text-xs text-zinc-500 mb-3">{m.settings_contacts_blurb()}</p>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-zinc-600">
          {m.settings_contacts_status({ count: statusQ.data?.count ?? 0, synced })}
        </span>
        <button
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        >
          {refresh.isPending ? m.settings_refreshing() : m.settings_refresh()}
        </button>
      </div>
      {refresh.data && (
        <p className="text-sm text-green-600 mt-2">{m.settings_synced_count({ count: refresh.data.count })}</p>
      )}
      {refresh.error && (
        <p className="text-sm text-red-600 mt-2">{(refresh.error as Error).message}</p>
      )}
    </Section>
  )
}

// ─── Calendar config ──────────────────────────────────────────────────────────

// Mirrors the backend applyPrefixFilter so the Settings panel can preview how a
// prefix turns an event title into a student name (space after prefix optional).
function prefixPreview(prefixText: string): { input: string; output: string }[] {
  const first  = prefixText.split('\n').map(s => s.trim()).filter(Boolean)[0] ?? 'Lesson:'
  const sample = 'Yael Cohen'
  const strip  = (title: string) => {
    const rest = title.toLowerCase().startsWith(first.toLowerCase())
      ? title.slice(first.length)
      : title
    return rest.replace(/^[\s:–—-]+/, '').trim() || sample
  }
  return [
    { input: `${first} ${sample}`, output: strip(`${first} ${sample}`) },
    { input: `${first}${sample}`,  output: strip(`${first}${sample}`) },
  ]
}

function CalendarConfig() {
  const queryClient = useQueryClient()

  const settingsQ = useQuery<CalendarSettings>({
    queryKey: ['calendar-settings'],
    queryFn:  getCalendarSettings,
  })

  const [loadCals, setLoadCals] = useState(false)
  const calendarsQ = useQuery<CalendarOption[]>({
    queryKey: ['calendars'],
    queryFn:  getCalendars,
    enabled:  loadCals,
  })

  const [calendarIds, setCalendarIds] = useState<string[]>([])
  const [prefixText, setPrefixText]   = useState('')
  const [saved, setSaved]             = useState(false)

  useEffect(() => {
    if (settingsQ.data) {
      setCalendarIds(settingsQ.data.calendarIds)
      setPrefixText(settingsQ.data.titlePrefixes.join('\n'))
    }
  }, [settingsQ.data])

  const save = useMutation({
    mutationFn: () => saveCalendarSettings({
      calendarIds,
      titlePrefixes: prefixText.split('\n').map(s => s.trim()).filter(Boolean),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-settings'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  const toggleCal = (id: string) =>
    setCalendarIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id])

  if (settingsQ.isLoading) return <p className="text-zinc-400">{m.loading()}</p>
  if (settingsQ.error) return <p className="text-red-600 text-sm">{(settingsQ.error as Error).message}</p>

  return (
    <>
      <Section title={m.settings_calendars()}>
        <p className="text-xs text-zinc-500 mb-3">{m.settings_calendars_blurb()}</p>

        {!loadCals ? (
          <button
            onClick={() => setLoadCals(true)}
            className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
          >
            {m.settings_load_calendars()}
          </button>
        ) : calendarsQ.isFetching ? (
          <p className="text-sm text-zinc-400">{m.settings_loading_calendars()}</p>
        ) : calendarsQ.error ? (
          <p className="text-sm text-red-600">{(calendarsQ.error as Error).message}</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {calendarsQ.data?.map(cal => (
              <label key={cal.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={calendarIds.includes(cal.id)}
                  onChange={() => toggleCal(cal.id)}
                />
                <span>{cal.summary}{cal.primary && <span className="text-zinc-400"> {m.settings_primary()}</span>}</span>
              </label>
            ))}
          </div>
        )}

        {!loadCals && calendarIds.length > 0 && (
          <p className="text-xs text-zinc-400 mt-3">{m.settings_currently_syncing({ ids: calendarIds.join(', ') })}</p>
        )}
      </Section>

      <Section title={m.settings_prefixes()}>
        <p className="text-xs text-zinc-500 mb-3">{m.settings_prefixes_blurb()}</p>
        <div className="flex flex-col sm:flex-row gap-3 items-start">
          <textarea
            rows={4}
            value={prefixText}
            onChange={e => setPrefixText(e.target.value)}
            placeholder={'Lesson:\nשיעור:'}
            className="border border-zinc-300 bg-transparent px-2 py-1.5 text-sm flex-1 w-full font-mono focus:outline-none focus:border-zinc-900"
          />
          <div className="w-full sm:w-72 shrink-0 border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs">
            <p className="font-medium text-zinc-600 mb-2">{m.settings_eg()}</p>
            {prefixPreview(prefixText).map((ex, i) => (
              <div key={i} className=" last:mb-0">
                <div className="font-mono text-zinc-500 break-all">"{ex.input}"</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <div className="flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="bg-zinc-900 text-white px-4 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {save.isPending ? m.settings_saving() : m.settings_save_calendar()}
        </button>
        {saved && <span className="text-sm text-green-600">{m.settings_saved()}</span>}
        {save.error && <span className="text-sm text-red-600">{(save.error as Error).message}</span>}
      </div>
    </>
  )
}
